import type { PropType } from 'vue';
import type { TIconButtonProps } from './TIconButton.types';
import { Icon } from '@iconify/vue';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent } from 'vue';
import { useTheme } from '../../composables/theme';
import { useStyles } from './TIconButton.styles';

const defaultProps = {
  variant: 'primary',
  size: 'medium',
  icon: 'academic-cap',
  type: 'button',
  color: 'primary',
};

export const TIconButton = defineComponent({
  name: 'TIconButton',
  props: {
    variant: String as PropType<TIconButtonProps['variant']>,
    size: String as PropType<TIconButtonProps['size']>,
    disabled: Boolean as PropType<TIconButtonProps['disabled']>,
    icon: String as PropType<TIconButtonProps['icon']>,
    type: String as PropType<TIconButtonProps['type']>,
    rounded: Boolean as PropType<TIconButtonProps['rounded']>,
    loading: Boolean as PropType<TIconButtonProps['loading']>,
    color: String as PropType<TIconButtonProps['color']>,
  },
  emits: ['click'],
  setup(_props, { attrs, emit }) {
    const props = computed(() => merge({}, defaultProps, _props, attrs));
    const color = computed(() => props.value.color);
    const { baseClass, iconClass } = useStyles(color);

    const theme = useTheme();
    const classes = computed(() => [
      baseClass.value,
      `${baseClass.value}--${props.value.variant}`,
      `${baseClass.value}--${props.value.size}`,
      {
        [`${baseClass.value}--rounded`]: props.value.rounded,
      },
    ]);
    const iconClasses = computed(() => [
      iconClass.value,
      `${iconClass.value}--${props.value.variant}`,
      `${iconClass.value}--${props.value.size}`,
      {
        [`${baseClass.value}--loading`]: props.value.loading,
      },
    ]);

    const handleClick = () => {
      if (!props.value.loading) {
        emit('click');
      }
    };

    return () => (
      <button
        class={classes.value}
        type={props.value.type}
        disabled={props.value.disabled}
        onClick={handleClick}
        {...attrs}
      >
        {props.value.loading
          ? (
              <Icon
                class={`${baseClass.value}__spinner`}
                icon="svg-spinners:90-ring-with-bg"
              />
            )
          : (
              <Icon
                icon={`${theme.value.iconSet}:${props.value.icon}`}
                class={iconClasses.value}
              />
            )}
      </button>
    );
  },
});
